import { inject, Injectable } from '@angular/core';
import type { InventoryTicket, StoredTicket } from '@seatix/check-in-domain';
import { TicketMapperService } from '@seatix/check-in-util-formatters';
import { IndexedDBService } from '../indexeddb/indexeddb.service';
import { SeatCategoryCacheService } from '../seat-category-cache/seat-category-cache.service';

@Injectable({
  providedIn: 'root',
})
export class TicketCacheService {
  readonly #db = inject(IndexedDBService);
  readonly #seatCategoryCache = inject(SeatCategoryCacheService);
  readonly #mapper = inject(TicketMapperService);

  async cacheInventory(
    eventId: number,
    ticketsMap: Record<string, InventoryTicket>,
  ): Promise<void> {
    const ticketEntries = Object.entries(ticketsMap);
    const cached_at = Date.now();

    const enrichedTickets: StoredTicket[] = await Promise.all(
      ticketEntries.map(async ([ticketIdKey, ticket]) => {
        const ticket_id = parseInt(ticketIdKey, 10);
        const seatCategory = await this.#seatCategoryCache.getSeatCategory(
          eventId,
          ticket.seat_category_id,
        );

        return {
          ticket_id,
          event_id: eventId,
          holder_name: ticket.holder_name,
          seat_label: ticket.seat_label,
          status: ticket.status,
          signature: ticket.signature,
          scan_id: ticket.scan_id,
          timestamp: ticket.timestamp,
          device_id: ticket.device_id,
          seat_category_id: ticket.seat_category_id,
          scanned_at: ticket.scanned_at,
          scanned_by: ticket.scanned_by ?? undefined,
          seat: this.#mapper.createSeatFromLabel(
            ticket.seat_label,
            seatCategory,
          )!,
          seat_category: seatCategory
            ? {
                name: seatCategory.name,
                color: seatCategory.color,
                id: seatCategory.id,
              }
            : null,
          cached_at,
        } satisfies StoredTicket;
      }),
    );

    await this.#db.ticketInventory.bulkPut(enrichedTickets);

    console.log(
      `[TicketCache] Cached ${enrichedTickets.length} tickets for event ${eventId}`,
    );
  }

  async getTicket(ticketId: number): Promise<StoredTicket | null> {
    const ticket = await this.#db.ticketInventory.get(ticketId);
    return ticket ?? null;
  }

  async hasTicket(ticketId: number): Promise<boolean> {
    const count = await this.#db.ticketInventory
      .where('ticket_id')
      .equals(ticketId)
      .count();
    return count > 0;
  }

  /**
   * Get ticket and verify it belongs to the specified event.
   * Returns { ticket, belongsToEvent } to allow pre-validation.
   */
  async getTicketForEvent(
    ticketId: number,
    eventId: number,
  ): Promise<{ ticket: StoredTicket | null; belongsToEvent: boolean }> {
    const ticket = await this.#db.ticketInventory.get(ticketId);

    if (!ticket) {
      return { ticket: null, belongsToEvent: false };
    }

    const belongsToEvent = ticket.event_id === eventId;

    if (!belongsToEvent) {
      console.warn(
        `[TicketCache] Ticket ${ticketId} belongs to event ${ticket.event_id}, not ${eventId}`,
      );
    }

    return { ticket, belongsToEvent };
  }

  async updateTicket(
    ticketId: number,
    updates: Partial<StoredTicket>,
  ): Promise<void> {
    ticketId = Number(ticketId);
    const existing = await this.getTicket(ticketId);

    if (existing) {
      await this.#db.ticketInventory.update(ticketId, {
        ...updates,
        scanned_at: Number(updates.scanned_at),
      });
    } else {
      await this.#db.ticketInventory.put({
        ...updates,
        ticket_id: ticketId,
        scanned_at: Number(updates.scanned_at),
      } as StoredTicket);
    }
  }

  async getEventInventory(eventId: number): Promise<StoredTicket[]> {
    return this.#db.ticketInventory.where('event_id').equals(eventId).toArray();
  }

  async checkRecentScan(
    eventId: number,
    ticketId: number,
  ): Promise<StoredTicket | null> {
    const ticket = await this.#db.ticketInventory.get(ticketId);

    if (!ticket || !ticket.scanned_at) {
      return null;
    }

    if (ticket.event_id !== eventId) {
      return null;
    }

    const age = Date.now() - ticket.scanned_at;
    const TTL_24_HOURS = 24 * 60 * 60 * 1000;

    return age <= TTL_24_HOURS ? ticket : null;
  }

  async getRecentScans(eventId: number, limit = 10): Promise<StoredTicket[]> {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;

    return this.#db.ticketInventory
      .where('[event_id+scanned_at]')
      .between([eventId, cutoff], [eventId, Date.now()])
      .reverse()
      .limit(limit)
      .toArray();
  }

  /**
   * Cache un ticket individual ya enriquecido (ej: desde WebSocket ticket.created)
   * No requiere re-enriquecimiento ya que viene con seat y seat_category completos
   */
  async cacheInventoryTicket(
    eventId: number,
    inventoryTicket: InventoryTicket,
  ): Promise<void> {
    const storedTicket: StoredTicket = {
      ...inventoryTicket,
      event_id: eventId,
      cached_at: Date.now(),
    };

    await this.#db.ticketInventory.put(storedTicket);

    console.log(
      `[TicketCache] Cached inventory ticket ${inventoryTicket.ticket_id} for event ${eventId}`,
    );
  }
}
